import React from "react";
import AceEditor from "react-ace";
import "./App.css";
import { defaultConfig } from "./defaultConfig.js";
import { defaultKeyValue } from "./defaultKeyValue.js";
import schema from "./message-schema.json";
import axios from "axios";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/mode-json";
import JSONTree from "react-json-tree";
import Ajv from "ajv";
import {
  Button,
  Columns,
  Container,
  Heading,
  Message,
  Section,
  Table,
} from "react-bulma-components";
import "bulma/css/bulma.min.css";

interface TransformationResponse {
  transformRecordSteps: TransformationStepRecord[];
  finalResult: TransformationStepRecord;
  messages: string[];
}
interface TransformationStepRecord {
  transform: string;
  record: Record;
}
interface Record {
  topic: string;
  key: any;
  value: any;
}
interface TransformationRequest {
  topic: string;
  config: any;
  key: any;
  value: any;
}

const ajv = new Ajv({ allErrors: true });

const defaultTransformationResponse: TransformationResponse = {
  transformRecordSteps: [],
  finalResult: {
    transform: "",
    record: { key: {}, value: {}, topic: "" },
  },
  messages: [],
};

const App = () => {
  const [transformationResponse, setTransformationResponse]: [
    TransformationResponse,
    (transformationResponse: TransformationResponse) => void
  ] = React.useState(defaultTransformationResponse);

  const [loading, setLoading]: [boolean, (loading: boolean) => void] =
    React.useState<boolean>(true);

  const [config, updateConfig]: [any, (config: any) => void] =
    React.useState<any>(JSON.stringify(defaultConfig, null, 2));

  const [keyValue, updateKeyValue]: [any, (keyValue: any) => void] =
    React.useState<any>(JSON.stringify(defaultKeyValue, null, 2));

  const [errors, updateErrors]: [string[], (errors: string[]) => void] =
    React.useState<string[]>([]);

  function debug() {
    updateErrors([]);
    var keyValueJson: any;
    var configJson: any;
    try {
      keyValueJson = JSON.parse(keyValue);
      configJson = JSON.parse(config);
    } catch (e) {
      updateErrors([e.message]);
      return;
    }

    const valid = ajv.validate(schema, keyValueJson);
    console.log(keyValueJson);
    if (!valid) {
      updateErrors([ajv.errorsText(ajv.errors, { dataVar: "Message Data" })]);
      return;
    }

    const transformRequest: TransformationRequest = {
      config: configJson,
      key: keyValueJson.key,
      value: keyValueJson.value,
      topic: keyValueJson.topic,
    };
    axios
      .post<TransformationResponse>(
        process.env.REACT_APP_DEBUG_API_URL ||
          "http://localhost:3000/transformdebug",
        transformRequest,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      )
      .then((response) => {
        setTransformationResponse(response.data);
        setLoading(false);
      })
      .catch((ex) => {
        updateErrors([ex.message]);
        setLoading(false);
      });
  }

  return (
    <Container>
      <Heading spaced={true}>Kafka Connect Transformation Debugger</Heading>
      <Section>
        <Columns>
          <Columns.Column>
            <Heading subtitle>Connector Config</Heading>
            <div className="">
              <AceEditor
                height="300px"
                width="700px"
                mode="json"
                theme="monokai"
                name="config"
                onChange={(value) => updateConfig(value)}
                value={config}
                editorProps={{ $blockScrolling: true }}
                setOptions={{
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion: true,
                  enableSnippets: true,
                }}
              />{" "}
            </div>
          </Columns.Column>
          <Columns.Column>
            <Heading subtitle>Example Message Key/Value</Heading>
            <AceEditor
              height="300px"
              width="500px"
              mode="json"
              theme="monokai"
              name="keyvalue"
              onChange={(value) => updateKeyValue(value)}
              value={keyValue}
              editorProps={{ $blockScrolling: true }}
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: true,
              }}
            />
          </Columns.Column>
        </Columns>
        <Columns>
          <Columns.Column>
            <Button color="primary" size="medium" outlined onClick={debug}>
              Show Transforms
            </Button>
          </Columns.Column>
        </Columns>
      </Section>
      <Container>
        <Message color="warning">{transformationResponse.messages}</Message>
        <Message color="danger">{errors}</Message>
      </Container>
      <Section>
        <Heading subtitle>Final Result</Heading>
        <Columns>
          <Columns.Column>
            <b>Topic</b>
            <p>{transformationResponse.finalResult.record.topic}</p>
          </Columns.Column>
          <Columns.Column>
            <h4>
              <b>Key</b>
            </h4>
            <Table>
              {Object.entries(
                transformationResponse.finalResult.record.key ?? {}
              ).map(([key, value]) => (
                <tr className="table-row">
                  <td key={`tablevalue-${key}`}>{key}</td>
                  <td key={`tablevalue-${value}`}>{JSON.stringify(value)}</td>
                </tr>
              ))}
            </Table>
          </Columns.Column>
          <Columns.Column>
            <h4>
              <b>Value</b>
            </h4>
            <Table>
              {Object.entries(
                transformationResponse.finalResult.record.value ?? {}
              ).map(([key, value]) => (
                <tr className="table-row">
                  <td key={`tablevalue-${key}`}>{key}</td>
                  <td key={`tablevalue-${value}`}>{JSON.stringify(value)}</td>
                </tr>
              ))}
            </Table>
          </Columns.Column>
        </Columns>
      </Section>
      <Section>
        <div>
          <Heading subtitle>Debug</Heading>
          <JSONTree
            data={transformationResponse.transformRecordSteps}
            shouldExpandNode={() => true}
          />
        </div>
      </Section>
      <Section>
        <Columns centered>
          <Columns.Column size="half">
            <a href="https://forms.gle/QWzfUAozwi3KR2L47" target="blank">
              Leave Feedback!
            </a>
          </Columns.Column>
        </Columns>
      </Section>
    </Container>
  );
};

export default App;
